"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { SidebarSection } from "@/components/navigation/navTypes";
interface WebSidebarSubmenuProps {
  readonly section: SidebarSection;
  readonly pathname: string | null;
  readonly onClose: () => void;
  readonly leftOffset?: number | undefined;
  readonly anchorTop?: number | undefined;
  readonly anchorHeight?: number | undefined;
  readonly triggerElement?: HTMLElement | null | undefined;
}
function WebSidebarSubmenu(t0) {
  const $ = _c(50);
  const {
    section,
    pathname,
    onClose,
    leftOffset,
    anchorTop,
    anchorHeight,
    triggerElement
  } = t0;
  const containerRef = useRef(null);
  const hasGlobalContext = typeof globalThis !== "undefined";
  const browserWindow = hasGlobalContext ? globalThis.window as Window | undefined : undefined;
  const browserDocument = hasGlobalContext ? globalThis.document as Document | undefined : undefined;
  let t1;
  if ($[0] !== anchorTop) {
    t1 = () => anchorTop ?? 16;
    $[0] = anchorTop;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [computedTop, setComputedTop] = useState(t1);
  let t2;
  if ($[2] !== section.subsections) {
    t2 = section.subsections?.reduce(_temp, 0) ?? 0;
    $[2] = section.subsections;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const totalItemCount = section.items.length + t2;
  let t3;
  if ($[4] !== onClose) {
    t3 = event => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    $[4] = onClose;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleKeyDown = t3;
  let t4;
  if ($[6] !== onClose || $[7] !== triggerElement) {
    t4 = event_0 => {
      const target = event_0.target;
      if (!(target instanceof Node)) {
        return;
      }
      const container = containerRef.current;
      if (container?.contains(target)) {
        return;
      }
      if (triggerElement?.contains(target)) {
        return;
      }
      onClose();
    };
    $[6] = onClose;
    $[7] = triggerElement;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleClickOutside = t4;
  let t5;
  let t6;
  if ($[9] !== handleClickOutside || $[10] !== handleKeyDown) {
    t5 = () => {
      if (browserWindow === undefined || browserDocument === undefined) {
        return;
      }
      browserWindow.addEventListener("keydown", handleKeyDown);
      browserDocument.addEventListener("mousedown", handleClickOutside);
      browserDocument.addEventListener("touchstart", handleClickOutside);
      return () => {
        browserWindow.removeEventListener("keydown", handleKeyDown);
        browserDocument.removeEventListener("mousedown", handleClickOutside);
        browserDocument.removeEventListener("touchstart", handleClickOutside);
      };
    };
    t6 = [browserWindow, browserDocument, handleKeyDown, handleClickOutside];
    $[9] = handleClickOutside;
    $[10] = handleKeyDown;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== anchorHeight || $[14] !== anchorTop) {
    t7 = () => {
      if (browserWindow && containerRef.current) {
        const menuRect = containerRef.current.getBoundingClientRect();
        const viewportHeight = browserWindow.innerHeight || 0;
        const anchorCenter = anchorTop !== undefined && anchorHeight !== undefined ? anchorTop + anchorHeight / 2 : (anchorTop ?? 16) + menuRect.height / 2;
        let top = anchorCenter - menuRect.height / 2;
        top = Math.max(16, Math.min(top, viewportHeight - menuRect.height - 16));
        setComputedTop(previous => previous === top ? previous : top);
      }
    };
    $[13] = anchorHeight;
    $[14] = anchorTop;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== anchorHeight || $[17] !== anchorTop || $[18] !== section.key || $[19] !== totalItemCount) {
    t8 = [browserWindow, anchorTop, anchorHeight, section.key, totalItemCount];
    $[16] = anchorHeight;
    $[17] = anchorTop;
    $[18] = section.key;
    $[19] = totalItemCount;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  useLayoutEffect(t7, t8);
  let t9;
  if ($[21] !== pathname) {
    t9 = href => pathname === href;
    $[21] = pathname;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const isActive = t9;
  if (browserDocument === undefined) {
    return null;
  }
  const portalTarget = browserDocument.body;
  if (!portalTarget) {
    return null;
  }
  const leftStyle = leftOffset === undefined ? "calc(var(--layout-margin, 0px) + 80px)" : `${leftOffset}px`;
  let t10;
  if ($[23] !== computedTop) {
    t10 = Number.isFinite(computedTop) ? `${computedTop}px` : "16px";
    $[23] = computedTop;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const topStyle = t10;
  let t11;
  if ($[25] !== isActive || $[26] !== onClose) {
    t11 = (item, t12) => {
      const nested = t12 === undefined ? false : t12;
      const active = isActive(item.href);
      return <Link key={item.href} href={item.href} className={`tw-touch-action-manipulation tw-group tw-flex tw-items-center tw-rounded-lg tw-no-underline tw-ring-offset-iron-950 tw-transition-all tw-duration-300 focus:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-iron-600 focus-visible:tw-ring-offset-1 ${nested ? "tw-min-h-10 tw-px-3 tw-py-2 tw-text-sm" : "tw-mb-1 tw-px-3 tw-py-2 tw-text-md"} ${active ? "tw-bg-iron-700 tw-font-semibold tw-text-white desktop-hover:hover:tw-text-white" : "tw-font-medium tw-text-iron-300 desktop-hover:hover:tw-bg-iron-700/50 desktop-hover:hover:tw-text-iron-50"}`} aria-current={active ? "page" : undefined} role="menuitem" onClick={onClose}><span className="tw-truncate">{item.name}</span></Link>;
    };
    $[25] = isActive;
    $[26] = onClose;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const renderLink = t11;
  let t12;
  if ($[28] !== leftStyle || $[29] !== topStyle) {
    t12 = {
      left: leftStyle,
      top: topStyle
    };
    $[28] = leftStyle;
    $[29] = topStyle;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const t13 = `${section.name} sub-navigation`;
  let t14;
  if ($[31] !== section.name) {
    t14 = <div className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-700 tw-px-6 tw-pb-2 tw-pt-4"><h3 className="tw-text-base tw-font-semibold tw-text-iron-50">{section.name}</h3></div>;
    $[31] = section.name;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== renderLink || $[34] !== section.items) {
    let t16;
    if ($[36] !== renderLink) {
      t16 = item_0 => renderLink(item_0);
      $[36] = renderLink;
      $[37] = t16;
    } else {
      t16 = $[37];
    }
    t15 = section.items.map(t16);
    $[33] = renderLink;
    $[34] = section.items;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[38] !== renderLink || $[39] !== section.items.length || $[40] !== section.subsections) {
    t16 = section.subsections?.map(subsection_0 => <div key={subsection_0.name} className={section.items.length > 0 ? "tw-mt-3" : "tw-mt-3 first:tw-mt-0"}><div className="tw-px-3 tw-pb-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.14em] tw-text-iron-500">{subsection_0.name}</div><div className="tw-ml-3 tw-border-0 tw-border-l tw-border-solid tw-border-iron-700 tw-pl-2">{subsection_0.items.map(item_1 => renderLink(item_1, true))}</div></div>);
    $[38] = renderLink;
    $[39] = section.items.length;
    $[40] = section.subsections;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t15 || $[43] !== t16) {
    t17 = <div className="tw-flex-1 tw-overflow-y-auto tw-p-3 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300">{t15}{t16}</div>;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t12 || $[46] !== t13 || $[47] !== t14 || $[48] !== t17) {
    t18 = createPortal(<div ref={containerRef} className="tailwind-scope tw-fixed tw-z-[95] tw-flex tw-max-h-[65vh] tw-w-64 tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-shadow-[0_20px_45px_rgba(7,7,11,0.7)]" style={t12} role="menu" aria-label={t13}>{t14}{t17}</div>, portalTarget);
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  return t18;
}
function _temp(count, subsection) {
  return count + subsection.items.length + 1;
}
export default WebSidebarSubmenu;